import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useCommunityProductMemberships } from 'admin-features/memberships/hooks';
import { useCommunityContext } from 'providers';
import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import {
  USER_PRODUCT_MEMBERSHIPS_QUERY_KEY,
  useUserProductMemberships,
} from 'features/memberships/hooks';
import { LoadingOverlay } from 'shared/components/ui';
import { useGrantCommunityMembershipMutation } from 'shared/graphql';
import { useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

interface Props {
  /**
   * Whether the modal is open.
   */
  open: boolean;

  /**
   * The ID of the user to grant membership to.
   */
  user: string;

  /**
   * Callback when the modal is closed.
   */
  onClose: () => void;
}

const GRANTING_MEMBERSHIP_MESSAGE_KEY = 'GRANTING_MEMBERSHIP';

export function GrantMembershipModal({ open, user: selectedUser, onClose }: Props) {
  const [selectedMembership, setSelectedMembership] = useState<string>();

  const queries = useQueryClient();

  const { selectedCommunity } = useCommunityContext();
  const { showError, showSuccess, showMessage, dismiss } = useToast();

  const { data: memberships, loading: isLoadingMemberships } = useCommunityProductMemberships();

  const { data: userMemberships } = useUserProductMemberships({ id: selectedUser });

  const availableMemberships = useMemo(() => {
    const userMembershipsSet = new Set(
      userMemberships?.map(userMembership => userMembership.membership.id),
    );

    return memberships.filter(membership => !userMembershipsSet.has(membership.id));
  }, [memberships, userMemberships]);

  const [grantMembership, { loading: isGranting }] = useGrantCommunityMembershipMutation({
    async onCompleted() {
      // Invalidate the user memberships query to refetch the user's memberships.
      await queries.invalidateQueries([
        USER_PRODUCT_MEMBERSHIPS_QUERY_KEY,
        selectedCommunity?.id,
        selectedUser,
      ]);
    },
  });

  const handleGrantMembership = async () => {
    if (!selectedUser || !selectedMembership || !selectedCommunity?.id) {
      return;
    }

    try {
      showMessage(`Granting membership to ${selectedUser}.`);

      await grantMembership({
        variables: {
          user: selectedUser,
          community: selectedCommunity.id,
          membership: selectedMembership,
        },
      });

      showSuccess(`Successfully granted membership`);
    } catch (error) {
      recordError(error);

      if (error instanceof Error) {
        showError(error.message);
      }
    } finally {
      dismiss(GRANTING_MEMBERSHIP_MESSAGE_KEY);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant Membership</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select a membership to grant to this user. This will give them access to exclusive content
          and features.
        </Typography>

        <LoadingOverlay
          loading={isLoadingMemberships}
          overlay={
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <CircularProgress />
            </div>
          }
        >
          <FormControl fullWidth>
            <InputLabel>Select Membership</InputLabel>

            <Select
              value={selectedMembership}
              onChange={event => setSelectedMembership(event.target.value)}
              label="Select Membership"
            >
              {availableMemberships.map(membership => (
                <MenuItem key={membership.id} value={membership.id}>
                  <Box display="flex" flexDirection="column">
                    <Typography>{membership.name}</Typography>

                    <Typography variant="caption" color="textSecondary">
                      {membership.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </LoadingOverlay>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isGranting}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={!selectedMembership || isGranting}
          onClick={handleGrantMembership}
        >
          {isGranting ? 'Granting...' : 'Grant Membership'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
